import React, { useEffect, useState } from "react";
import { api } from "../api.js";
import { useUI } from "../lib/theme.js";

type CatalogEntry = {
  provider: string;
  displayName: string;
  capabilities: string[];
  status: string;
  brandColor: string;
  isConnected: boolean;
  tos: { thirdPartyDisplay: "forbidden" | "consent_required"; aiModelTraining: boolean };
};

type Connection = {
  provider: string;
  scope: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
};

function unlockCopy(caps: string[], t: (k: string) => string): string {
  if (caps.includes("sport") && caps.includes("health")) return t("sources_unlock_both");
  if (caps.includes("health")) return t("sources_unlock_health");
  return t("sources_unlock_sport");
}

function CapabilityChip({ cap, t }: { cap: string; t: (k: string) => string }) {
  const label = cap === "sport" ? t("sources_sport") : t("sources_health");
  const cls =
    cap === "sport"
      ? "bg-blue-500/15 text-blue-600 dark:text-blue-300"
      : "bg-rose-500/15 text-rose-600 dark:text-rose-300";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>
      {label}
    </span>
  );
}

function SourceCard({
  entry,
  onConnect,
  onDisconnect,
  sportSources,
  sportPrimary,
  onSetPrimary,
  t,
}: {
  entry: CatalogEntry;
  onConnect: (p: string) => void;
  onDisconnect: (p: string) => void;
  sportSources: string[];
  sportPrimary: string;
  onSetPrimary: (p: string) => void;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const isSoon = entry.status === "soon";
  const isLegacy = entry.provider === "strava";
  const isPrimary = entry.provider === sportPrimary;
  const showPrimaryToggle =
    entry.isConnected &&
    entry.capabilities.includes("sport") &&
    sportSources.length >= 2;

  return (
    <div
      className={`rounded-xl border p-4 transition ${
        entry.isConnected
          ? "border-accent/40 bg-accent/5"
          : "border-line bg-panel2"
      } ${isSoon ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
          style={{ backgroundColor: entry.brandColor }}
        >
          {entry.displayName.slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{entry.displayName}</span>
            {isLegacy && (
              <span className="rounded-full bg-orange-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-orange-500">
                legacy
              </span>
            )}
            {entry.isConnected && (
              <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-green-600 dark:text-green-300">
                {t("sources_connected")}
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {entry.capabilities.map((c) => (
              <CapabilityChip key={c} cap={c} t={t} />
            ))}
          </div>
          <p className="mt-1 text-xs text-muted">{unlockCopy(entry.capabilities, t)}</p>
        </div>
      </div>

      {showPrimaryToggle && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-panel px-3 py-2">
          <input
            type="radio"
            id={`primary-${entry.provider}`}
            name="sportPrimary"
            checked={isPrimary}
            onChange={() => onSetPrimary(entry.provider)}
            className="accent-accent"
          />
          <label htmlFor={`primary-${entry.provider}`} className="text-xs text-muted cursor-pointer">
            {t("sources_sport_primary")}
          </label>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        {isSoon ? (
          <span className="rounded-lg border border-line px-3 py-1.5 text-xs text-muted">
            {t("sources_soon")}
          </span>
        ) : entry.isConnected ? (
          <button
            onClick={() => onDisconnect(entry.provider)}
            className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-600 transition hover:bg-red-500/20 dark:text-red-400"
          >
            {t("sources_disconnect")}
          </button>
        ) : (
          <button
            onClick={() => onConnect(entry.provider)}
            className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition hover:bg-accent/20"
          >
            {t("sources_connect")}
          </button>
        )}
      </div>
    </div>
  );
}

export function DataSources({ onReload }: { onReload?: () => void }) {
  const { t } = useUI();
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [connected, setConnected] = useState<Connection[]>([]);
  const [sportPrimary, setSportPrimary] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);

  async function load() {
    try {
      const data = await api.connections();
      setCatalog(data.catalog);
      setConnected(data.connected);
      const sportConns = data.connected.filter((c) =>
        data.catalog.find((m) => m.provider === c.provider)?.capabilities.includes("sport"),
      );
      if (sportConns.length > 0 && !sportConns.find((c) => c.provider === sportPrimary)) {
        setSportPrimary(sportConns[0]!.provider);
      }
    } catch {
      setError("Error cargando fuentes");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const sportSources = connected
    .filter((c) => catalog.find((m) => m.provider === c.provider)?.capabilities.includes("sport"))
    .map((c) => c.provider);

  function handleConnect(provider: string) {
    location.href = `/auth/${provider}`;
  }

  async function handleDisconnect(provider: string) {
    const meta = catalog.find((m) => m.provider === provider);
    const msg = t("sources_disconnect_confirm", { provider: meta?.displayName ?? provider });
    if (!confirm(msg)) return;

    setBusy(true);
    setError(null);
    try {
      const res = await api.disconnectProvider(provider);
      if (!res.ok) {
        if (res.error === "ultimo_proveedor") {
          setError(t("sources_disconnect_last"));
        } else {
          setError(res.error ?? "Error");
        }
        return;
      }
      await load();
      onReload?.();
    } finally {
      setBusy(false);
    }
  }

  async function handleSetPrimary(provider: string) {
    setSportPrimary(provider);
    try {
      await api.setSourcePrefs({ sportPrimary: provider });
    } catch {
      // best-effort
    }
  }

  async function handleUpload(file: File | null) {
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    setError(null);
    try {
      const res = await api.uploadActivity(file);
      if (!res.ok) {
        setUploadMsg(res.error ?? t("sources_upload_error"));
        return;
      }
      setUploadMsg(t("sources_upload_ok"));
      onReload?.();
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">{t("sources_title")}</h3>
      {error && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {sportSources.length >= 2 && (
        <p className="text-xs text-muted">{t("sources_sport_primary_hint")}</p>
      )}
      <div className={`space-y-2 ${busy ? "pointer-events-none opacity-60" : ""}`}>
        {catalog.map((entry) => (
          <SourceCard
            key={entry.provider}
            entry={entry}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            sportSources={sportSources}
            sportPrimary={sportPrimary}
            onSetPrimary={handleSetPrimary}
            t={t}
          />
        ))}
      </div>
      <div className={`rounded-xl border border-line bg-panel2 p-4 ${uploading ? "pointer-events-none opacity-60" : ""}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">{t("sources_upload_title")}</h4>
            <p className="mt-1 text-xs text-muted">{t("sources_upload_hint")}</p>
          </div>
          <label className="cursor-pointer rounded-lg border border-accent/40 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent transition hover:bg-accent/20">
            {t("sources_upload_cta")}
            <input
              type="file"
              accept=".gpx,.tcx,.fit"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                void handleUpload(file);
                e.currentTarget.value = "";
              }}
            />
          </label>
        </div>
        {uploadMsg && <p className="mt-3 text-xs text-muted">{uploadMsg}</p>}
      </div>
    </div>
  );
}
